import { useCallback, useEffect, useRef, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { HiOfficeBuilding } from "react-icons/hi";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import NavbarLanguageSelector from "./NavbarLanguageSelector";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import cutomiseIcon from "../../assets/img/cutomiseIcon.svg";
import AppIcons from "../../assets/icon/appIcons";
import { authSelector } from "../../components/Auth/AuthSlice";
import {
  toggleBulkSMSOverlay,
  toggleQuickSMSOverlay,
} from "../../components/Message/MessageSlice";
import { getProfileData } from "../../components/Profile/ProfileSlice";
import SubmissionReport from "../../components/Report/InnerReport/Submission";
import { userListGet } from "../../components/User/UserSlice";
import useModal from "../../hooks/useModal";
import {
  checkForWalletMin,
  getItemWithExpiry,
  hasRoles,
  isArray,
  isManager,
  isSeller,
} from "../../lib/utility";
import { middleware } from "../../redux/store";
import {
  getBalanceData,
  walletSelector,
} from "../../components/Wallet/WalletSlice";
import AddBalanceRequestModal from "./AddBalanceRequestModal";
import CustomizePanelFilter from "./CustomizePanel/CustomizePanelFilter";
import DownloadProgressModal from "./DownloadProgress/DownloadProgressModal";
import LowBalanceAlertModal from "./LowBalanceAlertModal";
import "./Navbar.scss";
import NotificationModal from "./Notifications/NotificationModal";
import {
  clearStatenotification,
  notificationSelector,
  setNotificationFetching,
  setNotificationsFromSocket,
} from "./Notifications/NotificationSlice";
// import useNotificationThrottle from "./Notifications/useNotificationThrottle";
import i18n, { LANGUAGE_LOCAL_STORAGE_KEY } from "../../i18n/config";
import { useWebSocketNotification } from "../../contexts/WebSocketNotification";
import useAbacAccess from "../../hooks/useAbacAccess";
import { useLayoutChrome } from "../LayoutChromeContext";

const Navbar = () => {
  const { t } = useTranslation("navbar");
  const [darkMode, setDarkMode] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(() => {
    const langMap = { en: "US", hi: "IN", ar: "SA", ru: "RU" };
    return langMap[i18n.language] || "US";
  });
  const [profileData, setProfileData] = useState(
    getItemWithExpiry("profileData")
  );
  const [showNotification, setShowNotification] = useState(false);
  const [showFileProgress, setShowFileProgress] = useState(false);
  const [showSubmissionReport, setShowSubmissionReport] = useState(false);
  const [lowBalanceAlert, setLowBalanceAlert] = useState(false);
  const [showCustomizePanel, setShowCustomizePanel] = useState(false);
  const [notifyId, setNotifyId] = useState();
  const [addBalanceLater, setAddBalanceLater] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  // Always start at 0 — never seed from globalThis (stale across logout/role switch)
  const [userList, setUserList] = useState([]);
  const [addBalanceId, setAddBalanceId] = useState();
  const [balanceUser, setBalanceUser] = useState(false);
  const [balanceData, setBalanceData] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const layoutChrome = useLayoutChrome();
  const {
    connected: notificationSocketConnected,
    registerEventHandler,
    fetchNotifications,
    updateNotification,
    deleteNotification: deleteNotificationWs,
    deleteAllNotifications: deleteAllNotificationsWs,
    pendingMutationRef,
  } = useWebSocketNotification();
  const { user } = useSelector(authSelector);
  // ABAC (multi-access members only; everyone else is allowed)
  const {
    canAccess,
    canAccessDefault,
    canAccessNavbar,
    canAccessNavbarAction,
    canAccessWalletUi,
    canAccessWalletPage,
  } = useAbacAccess();
  const canShowQuickMessageNav =
    canAccessNavbar("quick_message") &&
    canAccess("sms", "message", "quick_send", "view");
  const canShowBulkMessageNav =
    canAccessNavbar("bulk_message") &&
    canAccess("sms", "message", "bulk_campaign", "view");
  const canShowSubmissionReportNav = canAccessNavbar("submission_report");
  const goToWallet = useCallback(() => {
    if (!canAccessWalletPage) return;
    navigate("/wallet");
  }, [navigate, canAccessWalletPage]);
  const canThemeToggle = canAccessDefault("theme");
  const canLanguage = canAccessDefault("language");
  const canCustomizePanel = canAccessNavbar("customize_panel");
  const canViewNotifications = canAccessNavbar("notifications");
  const canClearNotifications = canAccessNavbarAction(
    "notifications",
    "clear_all"
  );
  const canDeleteNotification = canAccessNavbarAction("notifications", "delete");
  const canViewDownloads = canAccessNavbar("downloads");
  useEffect(() => {
    if (!canViewDownloads) setShowFileProgress(false);
  }, [canViewDownloads]);

  useEffect(() => {
    if (!canViewNotifications) setShowNotification(false);
  }, [canViewNotifications]);

  useEffect(() => {
    if (!canCustomizePanel) setShowCustomizePanel(false);
  }, [canCustomizePanel]);

  useEffect(() => {
    if (!canShowSubmissionReportNav) setShowSubmissionReport(false);
  }, [canShowSubmissionReportNav]);

  useEffect(() => {
    if (!canShowQuickMessageNav) dispatch(toggleQuickSMSOverlay(false));
  }, [canShowQuickMessageNav, dispatch]);

  useEffect(() => {
    if (!canShowBulkMessageNav) dispatch(toggleBulkSMSOverlay(false));
  }, [canShowBulkMessageNav, dispatch]);

  const { notificationData, isFetching: isFetchingNotification } =
    useSelector(notificationSelector);
  const { balanceData: newBalanceData, isSuccessBalance } =
    useSelector(walletSelector);
  const isEnhancedBalanceRole = hasRoles("tenant,superadmin,system", user);

  const isSupers = hasRoles("admin,superadmin,system,seller,manager", user);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("darkMode", newMode);
    applyTheme(newMode);
  };
  const applyTheme = (isDarkMode) => {
    const body = document.body;
    if (isDarkMode) {
      body.classList.add("dark-mode");
    } else {
      body.classList.remove("dark-mode");
    }
  };

  const flagToLangMap = {
    US: "en",
    IN: "hi",
    SA: "ar",
    RU: "ru",
  };

  const handleLanguageChange = (countryCode) => {
    const lang = flagToLangMap[countryCode];
    if (lang) {
      i18n.changeLanguage(lang);
      localStorage.setItem(LANGUAGE_LOCAL_STORAGE_KEY, lang);
      setSelectedFlag(countryCode);
    }
  };

  useEffect(() => {
    const langMap = { en: "US", hi: "IN", ar: "SA", ru: "RU" };
    const syncFlagWithLanguage = (lng) => {
      setSelectedFlag(langMap[lng] || "US");
    };

    syncFlagWithLanguage(i18n.language);
    i18n.on("languageChanged", syncFlagWithLanguage);
    return () => i18n.off("languageChanged", syncFlagWithLanguage);
  }, []);
  function handleQuickMessage(e) {
    e.preventDefault;
    if (!canShowQuickMessageNav) return;
    navigate("/message");
    dispatch(toggleQuickSMSOverlay(true));
  }
  function handleBulkMessage(e) {
    e.preventDefault;
    if (!canShowBulkMessageNav) return;
    navigate("/message");
    dispatch(toggleBulkSMSOverlay(true));
  }
  function handleSubmissionReportOpen(e) {
    e && e.preventDefault();
    if (!canShowSubmissionReportNav) return;
    setShowSubmissionReport(true);
  }
  function handleCustomizeFilter(e) {
    e && e.preventDefault();
    setShowCustomizePanel((prev) => !prev);
  }
  const handleNotificationModalOpen = useCallback(() => {
    if (!canViewNotifications) return;
    setShowNotification(true);
  }, [canViewNotifications]);

  const handleClearAllNotifications = useCallback(() => {
    dispatch(setNotificationFetching(true));
    deleteAllNotificationsWs();
  }, [dispatch, deleteAllNotificationsWs]);

  const handleFileProgressModalOpen = useCallback(() => {
    if (!canViewDownloads) return;
    setShowFileProgress(true);
  }, [canViewDownloads]);

  useEffect(() => {
    if (!layoutChrome?.register) return undefined;
    return layoutChrome.register({
      openNotifications: handleNotificationModalOpen,
      openDownloads: handleFileProgressModalOpen,
      openSubmissionReport: handleSubmissionReportOpen,
      openQuickMessage: handleQuickMessage,
      openBulkMessage: handleBulkMessage,
      openCustomizePanel: handleCustomizeFilter,
      goToWallet,
    });
  }, [
    layoutChrome,
    handleNotificationModalOpen,
    handleFileProgressModalOpen,
    goToWallet,
  ]);
  const getNotificationDataOnce = useCallback(
    (force) => {
      if (!notificationSocketConnected) return;
      dispatch(setNotificationFetching(true));
      fetchNotifications();
    },
    [dispatch, fetchNotifications, notificationSocketConnected]
  );
  const handleNotificationClick = useCallback(
    (id) => {
      if (!id)
        return console.error("Id is required to mark notification read");
      updateNotification(id, "yes", "no");
    },
    [updateNotification]
  );
  const deleteNotificationOnce = useCallback(
    (id) => {
      if (!id) return console.error("Id is required to delete Notification");
      dispatch(setNotificationFetching(true));
      deleteNotificationWs(id);
    },
    [dispatch, deleteNotificationWs]
  );
  const getProfileDataOnce = (force = false) => {
    if (
      force ||
      !profileData ||
      Object.keys(profileData).length === 0 ||
      globalThis.isFirstTime
    ) {
      dispatch(getProfileData()).then((action) =>
        middleware(action, "Profile Data", (action) => {
          setProfileData(action.payload);
          globalThis.profileData = action.payload;
          globalThis.isFirstTime = false;
        })
      );
    }
  };
  const balanceFetchedRef = useRef(false);
  const getBalanceDataOnce = useCallback(
    (force = false) => {
      // 1. Skip completely for Sellers and Managers
      if (isSeller(user) || isManager(user)) return;
      // 2. Avoid duplicate fetch if already cached and not forced
      if (
        !force &&
        balanceFetchedRef.current &&
        (newBalanceData || balanceData)
      )
        return;
      balanceFetchedRef.current = true;
      // 3. Dispatch with user credentials
      const username =
        user?.username ||
        user?.userName ||
        user?.systemId ||
        profileData?.userName ||
        profileData?.username;
      dispatch(
        getBalanceData({
          userId: user?.id,
          username,
        })
      ).then((action) => {
        middleware(action, "Balance Data");
      });
    },
    [dispatch, user, newBalanceData, balanceData, profileData]
  );
  const { clickRef, contRef, showModal, handleOpen } = useModal();
  // useNotificationThrottle(5, 30, getNotificationDataOnce);

  useEffect(() => {
    const isDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDarkMode);
    applyTheme(isDarkMode);
  }, []);

  const notificationSessionKey =
    user?.accessToken || user?.id || user?.username || user?.systemId || null;

  const initialSetupDone = useRef(false);
  useEffect(() => {
    if (!notificationSocketConnected) return;
    if (initialSetupDone.current) return;
    getProfileDataOnce();
    getNotificationDataOnce();
    getBalanceDataOnce();
    initialSetupDone.current = true;
  }, [
    notificationSocketConnected,
    notificationSessionKey,
    getProfileDataOnce,
    getNotificationDataOnce,
    getBalanceDataOnce,
  ]);

  const userDataGetOnce = () => {
    dispatch(userListGet("editUser")).then((action) =>
      middleware(action, "User List", (action) => {
        setUserList(action.payload.usernames);
      })
    );
  };

  useEffect(() => {
    if (isSupers) userDataGetOnce();
  }, []);

  // Drop previous role's badge/list immediately on logout or account switch.
  // Keep fetching true while logged in so the panel shows skeleton until data arrives.
  useEffect(() => {
    setNotificationCount(0);
    if (typeof globalThis !== "undefined") {
      globalThis.notificationCount = 0;
    }
    dispatch(clearStatenotification());
    initialSetupDone.current = false;
    if (notificationSessionKey) {
      dispatch(setNotificationFetching(true));
    }
  }, [notificationSessionKey, dispatch]);

useEffect(() => {
  const unregister = registerEventHandler((event) => {

    if (event?.type === "session-reset") {
      setNotificationCount(0);
      if (typeof globalThis !== "undefined") {
        globalThis.notificationCount = 0;
      }
      dispatch(clearStatenotification());
      return;
    }

    if (event?.type === "fetch") {
      const socketNotifications = isArray(event.notifications) ? event.notifications : [];
      dispatch(setNotificationsFromSocket([...socketNotifications].reverse()));
      dispatch(setNotificationFetching(false));
      pendingMutationRef.current = false; 

      const filteredNotifications = socketNotifications.filter(
        (element) =>
          element.title !== "Low Balance Alert" &&
          element.title !== "Add Balance Request Alert"
      );
      const totalUnread = filteredNotifications.filter((ntf) => !ntf.read).length;
      const latestTitle = filteredNotifications[0]?.title;

      if (latestTitle === "Profile Updated" && +globalThis.notificationCount !== totalUnread) {
        getProfileDataOnce(true);
      }
      if (latestTitle === "Low Balance Alert") {
        setNotifyId(filteredNotifications[0]?.id);
        setLowBalanceAlert(true);
      }
      if (latestTitle === "Add Balance Request Alert") {
        setAddBalanceLater(true);
        setAddBalanceId(filteredNotifications[0]);
      }

      setNotificationCount(totalUnread);
      if (globalThis) globalThis.notificationCount = totalUnread;
      return;
    }

    if (event?.type === "success") {
      getNotificationDataOnce(true);
      return;
    }

    if (event?.type === "live") {
      if (pendingMutationRef.current) {
        return;
      }
     
      getNotificationDataOnce(true);
      return;
    }

    if (event?.type === "error") {
      dispatch(setNotificationFetching(false));
      pendingMutationRef.current = false;
      getNotificationDataOnce(true);
    }
  });

  return () => unregister();
}, [dispatch, registerEventHandler, getProfileDataOnce, getNotificationDataOnce]); 
  useEffect(() => {
    const handleRefreshWallet = () => getBalanceDataOnce(true);
    window.addEventListener("refreshWallet", handleRefreshWallet);
    return () =>
      window.removeEventListener("refreshWallet", handleRefreshWallet);
  }, [getBalanceDataOnce]);

  useEffect(() => {
    if (isSuccessBalance && newBalanceData) {
      const data = newBalanceData?.data ?? newBalanceData;
      setBalanceData({
        wallets: data.walletBalance ?? data.wallets ?? data.balance,
        credits: data.credits,
        currency: data.currency,
        walletFlag:
          (
            data.walletType ||
            (data.walletBalance != null || data.wallets != null ? "wallet" : "")
          )?.toLowerCase() === "wallet"
            ? "yes"
            : "no",
        companyBalance:
          data.companyBalance !== undefined && data.companyBalance !== null
            ? data.companyBalance
            : undefined,
      });
    }
  }, [isSuccessBalance, newBalanceData]);

  const formatBalanceAmount = (value) =>
    value === null || value === undefined ? "0" : value.toString();

  const companyBalance = balanceData?.companyBalance;
  const hasCompanyCredits =
    companyBalance != null && companyBalance.companyCreditsBalance != null;
  const hasCompanyWallet =
    companyBalance != null && companyBalance.companyWalletBalance != null;
  const companyName =
    companyBalance?.companyName ||
    t("companyBalance.label", { defaultValue: "Company" });
  const isWalletEnabled = checkForWalletMin(balanceData?.walletFlag);
  const showCompanyCredits = hasCompanyCredits && (isEnhancedBalanceRole || !isWalletEnabled);
  const showCompanyWallet = hasCompanyWallet && (isEnhancedBalanceRole || isWalletEnabled);
  const showCompanyGroup = showCompanyCredits || showCompanyWallet;
  const companyWalletCurrency =
    balanceData?.currency && balanceData.currency !== "CRD"
      ? balanceData.currency
      : "";

  const renderBalanceBadge = ({
    amount,
    currency,
    tooltip,
    isCompany = false,
  }) => (
    <Tooltip
      title={tooltip}
      placement="bottom"
      mouseEnterDelay={0.2}
      getPopupContainer={() => document.body}
      overlayClassName="navbar-balance-tooltip"
      overlayInnerStyle={{ whiteSpace: "nowrap" }}
    >
      <div
        className={`navItem wallet${isCompany ? " company-wallet" : ""}`}
        onClick={isCompany ? undefined : goToWallet}
      >
        <button type="button" disabled={isCompany}>
          {isCompany ? (
            <HiOfficeBuilding className="company-wallet-icon" size={18} />
          ) : (
            <img src={AppIcons.crdEur} alt="" loading="lazy" />
          )}
          <span className="text-dark fs-16 px-1">
            {formatBalanceAmount(amount)}
          </span>{" "}
          {currency ? <span className="text-gray fs-12">{currency}</span> : null}
        </button>
      </div>
    </Tooltip>
  );

  return (
    <>
      <header>
        <nav>
       
          {canThemeToggle && (
            <div
              className="navItem nav-theme-toggle"
              data-tooltip={
                darkMode ? t("tooltips.lightMode") : t("tooltips.darkMode")
              }
              data-tooltip-location="bottom"
              onClick={toggleDarkMode}
            >
              <button>
                <img
                  src={AppIcons.darkLightMode}
                  alt=""
                  loading="lazy"
                />
              </button>
            </div>
          )}
          {hasRoles("admin", user) && canCustomizePanel && (
            <div
              className="navItem hide"
                data-tooltip={t("tooltips.customizePanel")}
              data-tooltip-location="bottom"
            >
              <button onClick={handleCustomizeFilter}>
                <img src={cutomiseIcon} alt="" loading="lazy" />
              </button>
            </div>
          )}
          {canViewNotifications && (
            <div
              className="navItem position-relative"
              data-tooltip={t("tooltips.notifications")}
              data-tooltip-location="bottom"
              onClick={handleNotificationModalOpen}
            >
              <button>
                <img src={AppIcons.notification} alt="" loading="lazy" />
              </button>
              {notificationCount !== 0 && (
                <span
                  style={{
                    right: -8,
                    top: -8,
                    backgroundColor: "tomato",
                  }}
                  className="position-absolute badge  rounded-circle"
                >
                  {notificationCount}
                </span>
              )}
            </div>
          )}
          {!isSeller(user) && !isManager(user) && (
            <>
              {canShowQuickMessageNav && (
              <div
                className="navItem hide"
                onClick={handleQuickMessage}
                data-tooltip={t("tooltips.quickMessage")}
                data-tooltip-location="bottom"
              >
                <button>
                  <img src={AppIcons.quickMessage} alt="" loading="lazy" />
                </button>
              </div>
              )}
              {canShowBulkMessageNav && (
              <div
                className="navItem hide"
                onClick={handleBulkMessage}
                data-tooltip={t("tooltips.bulkMessage")}
                data-tooltip-location="bottom"
              >
                <button>
                  <img src={AppIcons.bulkMessage} alt="" loading="lazy" />
                </button>
              </div>
              )}
              {canShowSubmissionReportNav && (
              <div
                className="navItem hide"
                onClick={handleSubmissionReportOpen}
                data-tooltip={t("tooltips.submissionReport")}
                data-tooltip-location="bottom"
              >
                <button>
                  <img src={AppIcons.submissionReport} alt="" loading="lazy" />
                </button>
              </div>
              )}
            </>
          )}
          {canViewDownloads && (
            <div
              className="navItem"
              data-tooltip={t("tooltips.fileDownloadProgress")}
              data-tooltip-location="bottom"
              onClick={handleFileProgressModalOpen}
            >
              <button>
                <img src={AppIcons.fileDownloadProgress} alt="" loading="lazy" />
              </button>
            </div>
          )}
          {canLanguage && (
            <div className="navItem language-selector">
              <NavbarLanguageSelector
                selectedFlag={selectedFlag}
                onSelect={handleLanguageChange}
              />
            </div>
          )}
          {!isSeller(user) && !isManager(user) && canAccessWalletUi && (
            <div className="navbar-balance-cluster">
              <div className="navbar-balance-group">
                {isEnhancedBalanceRole ? (
                  <>
                    {renderBalanceBadge({
                      amount: balanceData?.credits,
                      currency: "CRD",
                      tooltip: `${formatBalanceAmount(balanceData?.credits)} CRD`,
                    })}
                    {renderBalanceBadge({
                      amount: balanceData?.wallets,
                      currency: balanceData?.currency || "",
                      tooltip: `${formatBalanceAmount(balanceData?.wallets)}${
                        balanceData?.currency ? ` ${balanceData.currency}` : ""
                      }`,
                    })}
                  </>
                ) : isWalletEnabled ? (
                  renderBalanceBadge({
                    amount: balanceData?.wallets,
                    currency: balanceData?.currency || "",
                    tooltip: `${formatBalanceAmount(balanceData?.wallets)}${
                      balanceData?.currency ? ` ${balanceData.currency}` : ""
                    }`,
                  })
                ) : (
                  renderBalanceBadge({
                    amount: balanceData?.credits,
                    currency: "CRD",
                    tooltip: `${formatBalanceAmount(balanceData?.credits)} CRD`,
                  })
                )}
              </div>
              {showCompanyGroup && (
                <>
                  <div className="navbar-balance-divider" aria-hidden="true" />
                  <div className="navbar-balance-group navbar-balance-group--company">
                    {showCompanyCredits &&
                      renderBalanceBadge({
                        amount: companyBalance.companyCreditsBalance,
                        currency: "CRD",
                        isCompany: true,
                        tooltip: t("companyBalance.creditsTooltip", {
                          defaultValue: "{{name}} company credits: {{amount}} CRD",
                          name: companyName,
                          amount: formatBalanceAmount(
                            companyBalance.companyCreditsBalance
                          ),
                        }),
                      })}
                    {showCompanyWallet &&
                      renderBalanceBadge({
                        amount: companyBalance.companyWalletBalance,
                        currency: companyWalletCurrency,
                        isCompany: true,
                        tooltip: t("companyBalance.walletTooltip", {
                          defaultValue:
                            "{{name}} company wallet: {{amount}}{{currency}}",
                          name: companyName,
                          amount: formatBalanceAmount(
                            companyBalance.companyWalletBalance
                          ),
                          currency: companyWalletCurrency
                            ? ` ${companyWalletCurrency}`
                            : "",
                        }),
                      })}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="navItem more" ref={clickRef}>
            <button onClick={handleOpen}>
              <BsThreeDotsVertical color="#3f4d70" size={20} />
            </button>
          </div>
        </nav>

        {showModal && (
          <div className="moreOptions" ref={contRef}>
            {hasRoles("admin", user) && canCustomizePanel && (
              <div className="navItem hide" onClick={handleCustomizeFilter}>
                <button>
                  <img src={cutomiseIcon} alt="" loading="lazy" />
                  <span>{t("menu.customizePanel")}</span>
                </button>
              </div>
            )}
            {!isSeller(user) && !isManager(user) && (
              <>
                {canShowQuickMessageNav && (
                <div
                  className="navItem hide"
                  data-tooltip={t("tooltips.quickMessage")}
                  data-tooltip-location="left"
                  onClick={handleQuickMessage}
                >
                  <button>
                    <img src={AppIcons.quickMessage} alt="" loading="lazy" />
                  </button>
                </div>
                )}
                {canShowBulkMessageNav && (
                <div
                  className="navItem hide"
                  data-tooltip={t("tooltips.bulkMessage")}
                  data-tooltip-location="left"
                  onClick={handleBulkMessage}
                >
                  <button>
                    <img src={AppIcons.bulkMessage} alt="" loading="lazy" />
                  </button>
                </div>
                )}
                {canShowSubmissionReportNav && (
                <div
                  className="navItem hide"
                  data-tooltip={t("tooltips.submissionReport")}
                  data-tooltip-location="left"
                  onClick={handleSubmissionReportOpen}
                >
                  <button>
                    <img src={AppIcons.submissionReport} alt="" loading="lazy" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </header>
      {showNotification && canViewNotifications && (
        <NotificationModal
          canClearAll={canClearNotifications}
          canDelete={canDeleteNotification}
          isLoading={isFetchingNotification}
          close={() => setShowNotification(false)}
          show={showNotification}
          notificationData={notificationData}
          deleteNotificationOnce={deleteNotificationOnce}
          handleNotificationClick={handleNotificationClick}
          clearAllNotifications={handleClearAllNotifications} 
        />
      )}
      {showFileProgress && (
        <DownloadProgressModal
          close={() => setShowFileProgress(false)}
          show={showFileProgress}
        />
      )}

      {hasRoles("admin", user) && canCustomizePanel && showCustomizePanel && (
        <CustomizePanelFilter
          modal={showCustomizePanel}
          toggleModal={handleCustomizeFilter}
        />
      )}

      {hasRoles("admin,user", user) && lowBalanceAlert && (
        <LowBalanceAlertModal
          profileData={profileData}
          modal={lowBalanceAlert}
          toggleModal={() => setLowBalanceAlert(false)}
          deleteNotificationOnce={deleteNotificationOnce}
          notifyId={notifyId}
          userList={userList}
          handleNotificationClick={handleNotificationClick}
          balanceData={balanceData}
        />
      )}
      {hasRoles("superadmin,admin", user) && (
        <>
          {addBalanceLater && (
            <AddBalanceRequestModal
              modal={addBalanceLater}
              toggleModal={() => setAddBalanceLater(false)}
              profileData={profileData}
              deleteNotificationOnce={deleteNotificationOnce}
              addBalanceId={addBalanceId}
              setBalanceUser={setBalanceUser}
              balanceUser={balanceUser}
              lowBalanceNotification={notificationData.filter(
                (no) => no.title === "Add Balance Request Alert"
              )}
            />
          )}
        </>
      )}

      {!hasRoles("seller,manager", user) && (
        <>
          {showSubmissionReport && canShowSubmissionReportNav && (
            <SubmissionReport
              modal={showSubmissionReport}
              toggleModal={() => setShowSubmissionReport(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default Navbar;
